/**
 * Amidala CSPM API Service
 * Cloud Security Posture Management API client
 */
import { createTenantClient } from './apiClient';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api/amidala';

// Sending ``X-Tenant-ID`` on every Amidala call ensures write operations
// (e.g. collect-evidence) land in the tenant the user is currently viewing
// rather than an incidental Amidala scan tenant namespace.
const api = createTenantClient(API_URL, { withCredentials: true });

// ========== Tenant APIs ==========
export const tenantsAPI = {
  create: (data) => api.post('/tenants', data),
  list: () => api.get('/tenants'),
  get: (id) => api.get(`/tenants/${id}`),
  update: (id, data) => api.put(`/tenants/${id}`, data),
  addUser: (id, userData) => api.post(`/tenants/${id}/users`, userData),
  removeUser: (tenantId, userId) => api.delete(`/tenants/${tenantId}/users/${userId}`),
  seedSampleData: () => api.post('/seed-sample-data'),
};

// ========== Cloud Account APIs ==========
export const accountsAPI = {
  create: (tenantId, data) => api.post(`/tenants/${tenantId}/accounts`, data),
  list: (tenantId, params = {}) => api.get(`/tenants/${tenantId}/accounts`, { params }),
  get: (id) => api.get(`/accounts/${id}`),
  update: (id, data) => api.put(`/accounts/${id}`, data),
  delete: (id) => api.delete(`/accounts/${id}`),
  testConnection: (data) => api.post('/accounts/test-connection', data),
  scanAWS: (data) => api.post('/accounts/scan-aws', data),
  listRealScans: () => api.get('/real-scans'),
};

// ========== Scan APIs ==========
export const scansAPI = {
  create: (tenantId, data) => api.post(`/tenants/${tenantId}/scans`, data),
  list: (tenantId, params = {}) => api.get(`/tenants/${tenantId}/scans`, { params }),
  get: (id) => api.get(`/scans/${id}`),
  delete: (id) => api.delete(`/scans/${id}`),
  getFindings: (id, params = {}) => api.get(`/scans/${id}/findings`, { params }),
  getCompliance: (id, params = {}) => api.get(`/scans/${id}/compliance`, { params }),
  export: (id, format) => api.get(`/scans/${id}/export/${format}`, { responseType: 'blob' }),
};

// ========== Finding APIs ==========
export const findingsAPI = {
  listByTenant: (tenantId, params = {}) => api.get(`/tenants/${tenantId}/findings`, { params }),
  get: (id) => api.get(`/findings/${id}`),
  update: (id, data) => api.put(`/findings/${id}`, data),
  bulkUpdate: (data) => api.post('/findings/bulk-update', data),
};

// ========== Dashboard APIs ==========
export const dashboardAPI = {
  get: (tenantId) => api.get(`/tenants/${tenantId}/dashboard`),
};

// ========== Security Check APIs ==========
export const checksAPI = {
  list: (params = {}) => api.get('/checks', { params }),
  get: (id) => api.get(`/checks/${id}`),
};

// ========== Compliance APIs ==========
export const complianceAPI = {
  listFrameworks: () => api.get('/compliance/frameworks'),
  getTrend: (tenantId, weeks = 12) => api.get(`/tenants/${tenantId}/compliance/trend`, { params: { weeks } }),
  snapshot: (tenantId) => api.post(`/tenants/${tenantId}/compliance/snapshot`, {}),
  getCoverage: (tenantId) => api.get(`/tenants/${tenantId}/compliance/coverage`),
  exportPdf: (tenantId, framework) =>
    api.get(`/tenants/${tenantId}/compliance/report/${framework}/pdf`, { responseType: 'blob' }),
  updateFrameworkLabels: (tenantId, labels) =>
    api.put(`/tenants/${tenantId}/framework-labels`, { labels }),
};

// ========== Helper Functions ==========
export const downloadExport = async (scanId, format, filename) => {
  try {
    const response = await scansAPI.export(scanId, format);
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename || `cspm_scan_${scanId}.${format}`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Export failed:', error);
    throw error;
  }
};

// Severity colors
export const severityColors = {
  critical: { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-500' },
  high: { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-500' },
  medium: { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-500' },
  low: { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-500' },
  informational: { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-500' },
};

// Provider icons
export const providerIcons = {
  aws: '☁️',
  azure: '🔷',
  gcp: '🌐',
};

// Category icons
export const categoryIcons = {
  iam: '🔐',
  network: '🌐',
  storage: '💾',
  compute: '💻',
  logging: '📝',
  encryption: '🔒',
  database: '🗄️',
  container: '📦',
  serverless: '⚡',
  configuration: '⚙️',
};

// Framework short names
export const frameworkShortNames = {
  cis_aws: 'CIS AWS',
  cis_azure: 'CIS Azure',
  cis_gcp: 'CIS GCP',
  nist_800_53: 'NIST 800-53',
  pci_dss: 'PCI DSS',
  soc2: 'SOC 2',
  gdpr: 'GDPR',
  hipaa: 'HIPAA',
  iso_27001: 'ISO 27001',
};

// ========== Evidence Collection APIs ==========
export const evidenceAPI = {
  collectFromScan: (scanId, frameworks = []) =>
    api.post(`/collect-evidence/${scanId}`, { frameworks }),
  list: (params = {}) => api.get('/evidence', { params }),
  get: (evidenceId) => api.get(`/evidence/${evidenceId}`),
};

// ========== Demo Mode APIs ==========
export const demoModeAPI = {
  get: (tenantId) => api.get(`/tenants/${tenantId}/demo-mode`),
  set: (tenantId, enabled) => api.put(`/tenants/${tenantId}/demo-mode`, { enabled }),
};

// ========== External Connector APIs ==========
export const connectorsAPI = {
  listSupported: () => api.get('/connectors/types/supported'),
  list: (tenantId) => api.get(`/tenants/${tenantId}/connectors`),
  create: (tenantId, data) => api.post(`/tenants/${tenantId}/connectors`, data),
  get: (id) => api.get(`/connectors/${id}`),
  update: (id, data) => api.put(`/connectors/${id}`, data),
  delete: (id) => api.delete(`/connectors/${id}`),
  test: (id) => api.post(`/connectors/${id}/test`),
  sync: (id) => api.post(`/connectors/${id}/sync`),
};

// Connector source labels
export const connectorSourceLabels = {
  amidala_native: 'Amidala',
  security_hub: 'Security Hub',
  guardduty: 'GuardDuty',
  defender_for_cloud: 'Defender',
  gcp_scc: 'GCP SCC',
};

export const connectorSourceColors = {
  amidala_native: 'bg-indigo-100 text-indigo-800 border-indigo-300',
  security_hub: 'bg-orange-100 text-orange-800 border-orange-300',
  guardduty: 'bg-amber-100 text-amber-800 border-amber-300',
  defender_for_cloud: 'bg-blue-100 text-blue-800 border-blue-300',
  gcp_scc: 'bg-emerald-100 text-emerald-800 border-emerald-300',
};

export default api;
